import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, IndianRupee, CreditCard } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/data/content";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function PayDeposit({ enquiry, onPaid, className = "" }) {
  const [cfg, setCfg] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/config")
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({ configured: false, deposit_inr: 5000 }));
    api.get("/payments/stripe-config")
      .then((r) => setStripeCfg(r.data))
      .catch(() => setStripeCfg({ deposit_aed: 500 }));
  }, []);

  const handlePay = async () => {
    if (cfg && !cfg.configured) {
      toast.info("UPI payments are being activated — we'll confirm your booking on WhatsApp meanwhile.");
      window.open(
        `${CONTACT.whatsapp}?text=${encodeURIComponent(
          `Hello RK Tourism & Travel! I'd like to pay the booking advance for my ${enquiry.destination || "tour"} enquiry. Reference: ${enquiry.id}`
        )}`,
        "_blank"
      );
      return;
    }
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load the payment window");
      const { data } = await api.post("/payments/create-order", { enquiry_id: enquiry.id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "RK Tourism & Travels",
        description: "Booking Advance",
        order_id: data.order_id,
        prefill: { name: enquiry.name, contact: enquiry.phone, email: enquiry.email || "" },
        theme: { color: "#d4af37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            toast.success("Advance payment received — thank you!");
            onPaid?.();
          } catch {
            toast.error("Payment made but verification failed — we will confirm it manually.");
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleStripe = async () => {
    setStripeLoading(true);
    try {
      const { data } = await api.post("/payments/stripe-checkout", {
        enquiry_id: enquiry.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStripeLoading(false);
    }
  };

  return (
    <div className={`flex flex-col sm:flex-row flex-wrap gap-3 ${className}`}>
      <button
        onClick={handlePay}
        disabled={loading}
        data-testid={`pay-deposit-button-${enquiry.id}`}
        className="inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#0A1128] px-6 py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-300 hover:bg-[#f3e5ab] disabled:opacity-60"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <IndianRupee size={14} />}
        Pay ₹{(cfg?.deposit_inr || 5000).toLocaleString("en-IN")} Advance
      </button>
      <button
        onClick={handleStripe}
        disabled={stripeLoading}
        data-testid={`pay-stripe-button-${enquiry.id}`}
        className="inline-flex items-center justify-center gap-2 border border-[#d4af37] text-[#d4af37] px-6 py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-300 hover:bg-[#d4af37] hover:text-[#0A1128] disabled:opacity-60"
      >
        {stripeLoading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
        Pay AED {(stripeCfg?.deposit_aed || 500).toLocaleString("en-AE")} by Card
      </button>
    </div>
  );
}
